/**
 * MODULE: _shared/frontend-shell/src/pages/dashboard/dashboardRoleViews.ts
 * PURPOSE: Role-aware dashboard layout (Sprint 0b / PACK G) — map auth + sales persona to widget visibility.
 * DEPENDENCIES: core/auth contracts (UserRole)
 * PUBLIC_API: resolveDashboardPersona, getDashboardViewConfig, read/writeDashboardSalesPersona
 * SIDE_EFFECTS: localStorage key brazn.dashboard.salesPersona
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-22 [2.2.1470] — showTeamAccountsCard for manager persona; AE/default show my Needs Attention card.
 * RECENT_PRIOR: 2026-05-21 [2.2.1098] — PACK G role-aware default dashboard views.
 */

import type { User } from '@core/auth';

export type DashboardPersona = 'default' | 'sdr' | 'ae' | 'manager' | 'admin';

export type DashboardSalesPersona = 'sdr' | 'ae' | 'manager';

export const DASHBOARD_SALES_PERSONA_KEY = 'brazn.dashboard.salesPersona';
export const DASHBOARD_PERSONA_CHANGED_EVENT = 'brazn.dashboard.persona-changed';

const ADMIN_PLATFORM_ROLES = new Set(['super_admin', 'tenant_admin', 'admin']);

export interface DashboardViewConfig {
  persona: DashboardPersona;
  title: string;
  description: string;
  showPipelineCards: boolean;
  showAccountsCard: boolean;
  /** Manager persona — team patch accounts needing attention (portfolio teamScope). */
  showTeamAccountsCard: boolean;
  showTemplateGenerator: boolean;
  showActivityFeed: boolean;
  showSdrQuickLinks: boolean;
  showAdminWorkspace: boolean;
  showMyWidgets: boolean;
  preferInsightsRail: boolean;
}

const VIEW_BY_PERSONA: Record<DashboardPersona, DashboardViewConfig> = {
  default: {
    persona: 'default',
    title: 'Revenue dashboard',
    description: 'Full pipeline, accounts, and assistant widgets.',
    showPipelineCards: true,
    showAccountsCard: true,
    showTeamAccountsCard: false,
    showTemplateGenerator: true,
    showActivityFeed: true,
    showSdrQuickLinks: false,
    showAdminWorkspace: false,
    showMyWidgets: true,
    preferInsightsRail: true,
  },
  sdr: {
    persona: 'sdr',
    title: 'SDR workspace',
    description: 'Calls, sequences, and next-best-action focus.',
    showPipelineCards: false,
    showAccountsCard: false,
    showTeamAccountsCard: false,
    showTemplateGenerator: false,
    showActivityFeed: true,
    showSdrQuickLinks: true,
    showAdminWorkspace: false,
    showMyWidgets: true,
    preferInsightsRail: true,
  },
  ae: {
    persona: 'ae',
    title: 'Account executive',
    description: 'Pipeline health, deal intelligence, and next steps.',
    showPipelineCards: true,
    showAccountsCard: true,
    showTeamAccountsCard: false,
    showTemplateGenerator: true,
    showActivityFeed: true,
    showSdrQuickLinks: false,
    showAdminWorkspace: false,
    showMyWidgets: true,
    preferInsightsRail: true,
  },
  manager: {
    persona: 'manager',
    title: 'Manager overview',
    description: 'Team forecast, at-risk deals, and quota coverage.',
    showPipelineCards: true,
    showAccountsCard: false,
    showTeamAccountsCard: true,
    showTemplateGenerator: false,
    showActivityFeed: true,
    showSdrQuickLinks: false,
    showAdminWorkspace: false,
    showMyWidgets: true,
    preferInsightsRail: true,
  },
  admin: {
    persona: 'admin',
    title: 'Workspace admin',
    description: 'Tenant health, integrations, and platform usage.',
    showPipelineCards: false,
    showAccountsCard: false,
    showTeamAccountsCard: false,
    showTemplateGenerator: false,
    showActivityFeed: true,
    showSdrQuickLinks: false,
    showAdminWorkspace: true,
    showMyWidgets: false,
    preferInsightsRail: false,
  },
};

export function readDashboardSalesPersona(): DashboardSalesPersona | null {
  if (typeof window === 'undefined') return null;
  const raw = (window.localStorage.getItem(DASHBOARD_SALES_PERSONA_KEY) || '').trim().toLowerCase();
  if (raw === 'sdr' || raw === 'ae' || raw === 'manager') return raw;
  return null;
}

export function writeDashboardSalesPersona(persona: DashboardSalesPersona | null): void {
  if (typeof window === 'undefined') return;
  if (!persona) {
    window.localStorage.removeItem(DASHBOARD_SALES_PERSONA_KEY);
  } else {
    window.localStorage.setItem(DASHBOARD_SALES_PERSONA_KEY, persona);
  }
  window.dispatchEvent(new CustomEvent(DASHBOARD_PERSONA_CHANGED_EVENT));
}

export function resolveDashboardPersona(user: User | null | undefined): DashboardPersona {
  const role = (user?.role || '').trim();
  if (ADMIN_PLATFORM_ROLES.has(role)) {
    return 'admin';
  }
  const sales = readDashboardSalesPersona();
  if (sales) return sales;
  return 'default';
}

export function getDashboardViewConfig(persona: DashboardPersona): DashboardViewConfig {
  return VIEW_BY_PERSONA[persona] ?? VIEW_BY_PERSONA.default;
}
